import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  CircleAlert,
  CloudUpload,
  Database,
  DatabaseBackup,
  HardDriveDownload,
  HeartPulse,
  RefreshCw,
  RotateCcw,
  ShieldCheck
} from 'lucide-react';
import { readToken, superadminTokenKey } from '../../app/session';
import { ApiError, apiRequest } from '../../lib/api';
import { statusLabel } from './types';

interface InfrastructureItem {
  companyId: string;
  companyName: string;
  companyStatus: string;
  databaseName: string;
  host: string;
  port: number;
  schemaVersion: string;
  healthStatus: string;
  healthCheckedAt: string | null;
  lastBackup: {
    status: string;
    createdAt: string | null;
    fileSizeBytes: number | null;
    fileName: string | null;
  } | null;
}

interface PlatformBackup {
  id: string;
  status: string;
  backupSetName: string | null;
  databaseCount: number;
  storageFileCount: number;
  totalSizeBytes: number | null;
  verificationStatus: string;
  verifiedAt: string | null;
  restoreTestStatus: string;
  restoreTestedAt: string | null;
  restoreTestMessage: string | null;
  offsiteStatus: string;
  offsiteCopiedAt: string | null;
  offsiteMessage: string | null;
  createdAt: string;
}

function formatBytes(value: number | null): string {
  if (!value) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let current = value;
  let unit = 0;
  while (current >= 1024 && unit < units.length - 1) {
    current /= 1024;
    unit += 1;
  }
  return `${current.toFixed(unit ? 1 : 0)} ${units[unit]}`;
}

function formatDate(value: string | null): string {
  if (!value) return 'Nikad';
  return new Intl.DateTimeFormat('hr-HR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
}

function reliabilityLabel(value: string): string {
  const labels: Record<string, string> = {
    NOT_VERIFIED: 'Nije provjeren',
    VERIFIED: 'Integritet potvrđen',
    NOT_RUN: 'Nije testiran',
    RUNNING: 'Test je u tijeku',
    SUCCEEDED: 'Uspješno',
    FAILED: 'Neuspjelo',
    NOT_CONFIGURED: 'Nije podešeno',
    PENDING: 'Kopiranje u tijeku'
  };
  return labels[value] ?? statusLabel(value);
}

export function InfrastructurePage() {
  const token = readToken(superadminTokenKey);
  const [items, setItems] = useState<InfrastructureItem[]>([]);
  const [platformBackup, setPlatformBackup] = useState<PlatformBackup | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function load() {
    try {
      const [infrastructure, platform] = await Promise.all([
        apiRequest<{ items: InfrastructureItem[] }>(
          '/api/v1/superadmin/infrastructure',
          { accessToken: token }
        ),
        apiRequest<{ backup: PlatformBackup | null }>(
          '/api/v1/superadmin/backups/platform/latest',
          { accessToken: token }
        )
      ]);
      setItems(infrastructure.items);
      setPlatformBackup(platform.backup);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Baze nisu dostupne.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function check(companyId?: string) {
    const key = companyId ? `check-${companyId}` : 'check-all';
    setBusy(key);
    setError('');
    setMessage('');
    try {
      await apiRequest(
        companyId
          ? `/api/v1/superadmin/infrastructure/${companyId}/check`
          : '/api/v1/superadmin/infrastructure/check-all',
        { method: 'POST', accessToken: token }
      );
      await load();
      setMessage(companyId ? 'Baza je provjerena.' : 'Provjerene su sve baze.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Provjera nije uspjela.');
    } finally {
      setBusy('');
    }
  }

  async function backup(companyId?: string) {
    const key = companyId ? `backup-${companyId}` : 'backup-all';
    setBusy(key);
    setError('');
    setMessage('');
    try {
      const response = await apiRequest<{ items?: Array<{ status: string }> }>(
        companyId
          ? `/api/v1/superadmin/backups/${companyId}`
          : '/api/v1/superadmin/backups/run-all',
        { method: 'POST', accessToken: token }
      );
      await load();
      const failed = response.items?.filter((item) => item.status === 'FAILED').length ?? 0;
      setMessage(
        failed
          ? `Backup tenant baza je dovršen, ali ${failed} baza nije uspjelo.`
          : 'Sigurnosna kopija tenant baze uspješno je izrađena.'
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Backup nije uspio.');
    } finally {
      setBusy('');
    }
  }

  async function createFullBackup() {
    setBusy('platform-backup');
    setError('');
    setMessage('');
    try {
      await apiRequest('/api/v1/superadmin/backups/platform', {
        method: 'POST',
        accessToken: token
      });
      await load();
      setMessage('Potpuni backup platforme je izrađen: control baza, tenant baze i storage.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Potpuni backup nije uspio.');
    } finally {
      setBusy('');
    }
  }

  async function verifyFullBackup() {
    if (!platformBackup) return;
    setBusy('platform-verify');
    setError('');
    setMessage('');
    try {
      await apiRequest(`/api/v1/superadmin/backups/platform/${platformBackup.id}/verify`, {
        method: 'POST',
        accessToken: token
      });
      await load();
      setMessage('Provjereni su manifest, checksumovi, gzip baze i sve storage datoteke.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Provjera backupa nije uspjela.');
    } finally {
      setBusy('');
    }
  }

  async function testRestore() {
    if (!platformBackup) return;
    const confirmed = window.confirm(
      'Restore test će privremeno vratiti svaku bazu u izoliranu testnu bazu, provjeriti tablice i zatim je obrisati. Produkcijski podaci se ne mijenjaju. Pokrenuti test?'
    );
    if (!confirmed) return;
    setBusy('platform-restore-test');
    setError('');
    setMessage('');
    try {
      await apiRequest(
        `/api/v1/superadmin/backups/platform/${platformBackup.id}/test-restore`,
        { method: 'POST', accessToken: token }
      );
      await load();
      setMessage('Restore test je prošao. Backup je dokazano moguće vratiti.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Restore test nije uspio.');
    } finally {
      setBusy('');
    }
  }

  async function copyOffsite() {
    if (!platformBackup) return;
    setBusy('platform-offsite');
    setError('');
    setMessage('');
    try {
      await apiRequest(`/api/v1/superadmin/backups/platform/${platformBackup.id}/offsite`, {
        method: 'POST',
        accessToken: token
      });
      await load();
      setMessage('Offsite kopija je izrađena i provjerena na odvojenoj lokaciji.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Offsite kopiranje nije uspjelo.');
    } finally {
      setBusy('');
    }
  }

  const unhealthy = items.filter((item) => item.healthStatus === 'UNHEALTHY').length;
  const withoutBackup = items.filter(
    (item) => !item.lastBackup || item.lastBackup.status !== 'SUCCEEDED'
  ).length;

  return (
    <>
      <header className="page-header">
        <div>
          <span className="eyebrow">Tehničko zdravlje i oporavak</span>
          <h1>Baze i backup</h1>
          <p>Backup nije potvrđen dok integritet i stvarni povrat nisu uspješno testirani.</p>
        </div>
        <div className="header-actions">
          <button className="button button--secondary" onClick={() => void check()} disabled={Boolean(busy)}>
            <HeartPulse /> Provjeri sve baze
          </button>
          <button className="button button--primary" onClick={() => void createFullBackup()} disabled={Boolean(busy)}>
            <DatabaseBackup /> {busy === 'platform-backup' ? 'Izrađujem…' : 'Potpuni backup'}
          </button>
        </div>
      </header>

      <section className="admin-kpis">
        <article><span>Tenant baze</span><strong>{items.length}</strong><Database /></article>
        <article><span>Zdrave baze</span><strong>{items.length - unhealthy}</strong><CheckCircle2 /></article>
        <article><span>Nedostupne</span><strong>{unhealthy}</strong><CircleAlert /></article>
        <article><span>Bez DB backupa</span><strong>{withoutBackup}</strong><HardDriveDownload /></article>
      </section>

      {message && <div className="form-success page-message">{message}</div>}
      {error && <div className="form-error page-message">{error}</div>}

      <section className="panel">
        <div className="panel__toolbar">
          <div>
            <h2>Potpuni backup platforme</h2>
            <p className="muted">Control baza, sve tenant baze, logotipi, dokumenti i operativni prilozi u jednom backup setu.</p>
          </div>
          <button className="icon-button" onClick={() => void load()}><RefreshCw /></button>
        </div>
        {platformBackup ? (
          <div className="database-list">
            <article>
              <span className={`database-health database-health--${platformBackup.status === 'SUCCEEDED' ? 'healthy' : 'unhealthy'}`}><DatabaseBackup /></span>
              <div className="database-main">
                <strong>{formatDate(platformBackup.createdAt)}</strong>
                <small>{platformBackup.databaseCount} baza · {platformBackup.storageFileCount} datoteka · {formatBytes(platformBackup.totalSizeBytes)}</small>
              </div>
              <div className="database-meta">
                <span>Integritet</span>
                <strong>{reliabilityLabel(platformBackup.verificationStatus)}</strong>
                <small>{formatDate(platformBackup.verifiedAt)}</small>
              </div>
              <div className="database-meta">
                <span>Restore test</span>
                <strong>{reliabilityLabel(platformBackup.restoreTestStatus)}</strong>
                <small>{formatDate(platformBackup.restoreTestedAt)}</small>
              </div>
              <div className="database-meta">
                <span>Offsite kopija</span>
                <strong>{reliabilityLabel(platformBackup.offsiteStatus)}</strong>
                <small>{formatDate(platformBackup.offsiteCopiedAt)}</small>
              </div>
              <div className="database-actions">
                <button className="button button--secondary" onClick={() => void copyOffsite()} disabled={Boolean(busy) || platformBackup.status !== 'SUCCEEDED'}>
                  <CloudUpload /> {busy === 'platform-offsite' ? 'Kopiram…' : 'Offsite kopija'}
                </button>
                <button className="button button--secondary" onClick={() => void verifyFullBackup()} disabled={Boolean(busy) || platformBackup.status !== 'SUCCEEDED'}>
                  <ShieldCheck /> {busy === 'platform-verify' ? 'Provjeravam…' : 'Provjeri integritet'}
                </button>
                <button className="button button--primary" onClick={() => void testRestore()} disabled={Boolean(busy) || platformBackup.status !== 'SUCCEEDED'}>
                  <RotateCcw /> {busy === 'platform-restore-test' ? 'Vraćam testno…' : 'Testiraj povrat'}
                </button>
              </div>
            </article>
            {platformBackup.restoreTestMessage && (
              <div className="muted">{platformBackup.restoreTestMessage}</div>
            )}
            {platformBackup.offsiteMessage && (
              <div className="muted">Offsite: {platformBackup.offsiteMessage}</div>
            )}
          </div>
        ) : (
          <div className="table-empty">Potpuni backup platforme još nije izrađen.</div>
        )}
      </section>

      <section className="panel">
        <div className="panel__toolbar">
          <div>
            <h2>Pojedinačne tenant baze</h2>
            <p className="muted">Brzi DB backup ne uključuje dokumente ni control bazu. Za potpuni oporavak koristi gornju opciju.</p>
          </div>
          <button className="button button--secondary" onClick={() => void backup()} disabled={Boolean(busy)}>
            <HardDriveDownload /> {busy === 'backup-all' ? 'Izrađujem…' : 'Backup svih tenant baza'}
          </button>
        </div>
        <div className="database-list">
          {items.map((item) => (
            <article key={item.companyId}>
              <span className={`database-health database-health--${item.healthStatus.toLowerCase()}`}><Database /></span>
              <div className="database-main">
                <strong>{item.companyName}</strong>
                <small>{item.databaseName} · schema {item.schemaVersion}</small>
              </div>
              <div className="database-meta"><span>Zdravlje</span><strong>{statusLabel(item.healthStatus)}</strong><small>{formatDate(item.healthCheckedAt)}</small></div>
              <div className="database-meta"><span>Zadnji DB backup</span><strong>{item.lastBackup ? statusLabel(item.lastBackup.status) : 'Nema'}</strong><small>{item.lastBackup ? `${formatDate(item.lastBackup.createdAt)} · ${formatBytes(item.lastBackup.fileSizeBytes)}` : 'Nije izrađen'}</small></div>
              <div className="database-actions">
                <button className="button button--secondary" onClick={() => void check(item.companyId)} disabled={Boolean(busy)}><HeartPulse /> {busy === `check-${item.companyId}` ? 'Provjeravam…' : 'Provjeri'}</button>
                <button className="button button--primary" onClick={() => void backup(item.companyId)} disabled={Boolean(busy)}><HardDriveDownload /> {busy === `backup-${item.companyId}` ? 'Radim backup…' : 'DB backup'}</button>
              </div>
            </article>
          ))}
          {!items.length && <div className="table-empty">Još nema otvorenih tenant baza.</div>}
        </div>
      </section>
    </>
  );
}
